import React, { useCallback, useEffect, useState } from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { fetchPendingSettlements, retryPendingSettlement } from '../../services/walletApi';
import {
  TreasuryUltraAlert,
  TreasuryUltraBtn,
  TreasuryUltraRow,
  TreasuryUltraShell,
} from './TreasuryUltraShell';

export default function PendingSettlements({ onQueueChange = null, onRetryDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPendingSettlements('pending_transfer');
      const list = data.items || [];
      setItems(list);
      if (onQueueChange) onQueueChange(list.length);
    } catch {
      setItems([]);
      if (onQueueChange) onQueueChange(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (id) => {
    setBusyId(id);
    setMessage('');
    try {
      const result = await retryPendingSettlement(id);
      if (result.duplicate) {
        setMessage('Already settled (duplicate).');
      } else {
        setMessage(`Transferred ₹${Number(result.settlement?.net_owner_amount || 0).toLocaleString('en-IN')} to owner wallet.`);
      }
      await load();
      if (onRetryDone) await onRetryDone();
    } catch (err) {
      setMessage(err.message || 'Transfer failed — check treasury balance.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <TreasuryUltraShell
        title="Pending Owner Transfers"
        subtitle="Work completed but treasury was low — retry when balance is sufficient"
        icon={String.fromCodePoint(0x23F3)}
        accent="amber"
      >
        <div className="de-treasury-ultra-skeleton h-20" />
      </TreasuryUltraShell>
    );
  }

  return (
    <TreasuryUltraShell
      title="Pending Owner Transfers"
      subtitle="Work completed but treasury was low — retry when balance is sufficient"
      icon={String.fromCodePoint(0x23F3)}
      accent="amber"
      badge={items.length || '0'}
      scrollable={items.length > 2}
      scrollMaxHeightClass="max-h-[min(320px,40vh)]"
      showScrollHint={items.length > 2}
      scrollHint="Scroll pending transfers"
    >
      {message ? (
        <TreasuryUltraAlert tone={message.toLowerCase().includes('fail') ? 'error' : 'success'}>
          {message}
        </TreasuryUltraAlert>
      ) : null}

      {items.length === 0 ? (
        <TreasuryUltraAlert tone="info">No pending treasury → owner transfers. All settlements are up to date.</TreasuryUltraAlert>
      ) : (
        items.map((row) => (
          <TreasuryUltraRow
            key={row.id}
            tone="amber"
            title={`${row.booking_ref} · Net ₹${Number(row.net_owner_amount).toLocaleString('en-IN')}`}
            meta={`Owner: ${row.owner?.name || row.owner_id} · Client: ${row.client?.name || row.client_id} · ${new Date(row.created_at).toLocaleString('en-IN')}`}
            actions={(
              <TreasuryUltraBtn
                variant="payout"
                disabled={busyId === row.id}
                onClick={() => handleRetry(row.id)}
              >
                {busyId === row.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ArrowUpRight className="h-3.5 w-3.5" />
                )}
                Transfer
              </TreasuryUltraBtn>
            )}
          />
        ))
      )}
    </TreasuryUltraShell>
  );
}
